import { useEffect, useState } from "react";
import API from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { toast } from "sonner";
import { Broom, Spinner, Warning } from "@phosphor-icons/react";
import { TestDataTenantRow, TestDataUserRow } from "./TestDataRows";

export function TestDataCleanupDialog({ open, onClose, onDone }) {
  const [scan, setScan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tenantIds, setTenantIds] = useState(new Set());
  const [userIds, setUserIds] = useState(new Set());
  const [clearOrphans, setClearOrphans] = useState(true);
  const [extra, setExtra] = useState([]);
  const [email, setEmail] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const load = async (extras) => {
    setLoading(true);
    try {
      const { data } = await API.get("/platform/test-data/scan", { params: { extra: extras.join(",") } });
      setScan(data);
      setTenantIds(new Set(data.tenants.filter((t) => !t.is_sandbox).map((t) => t.id)));
      setUserIds(new Set(data.users.filter((u) => !u.is_platform_admin || u.added_by_you).map((u) => u.id)));
      if (data.not_found?.length) toast.warning(`No login found for ${data.not_found.join(", ")}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't scan for test data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open) { setResult(null); setConfirm(""); setExtra([]); load([]); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (set, setter, id) => setter((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const addEmail = () => { const e = email.trim().toLowerCase(); if (!e) return; const next = [...extra, e]; setExtra(next); setEmail(""); load(next); };
  const orphanTotal = scan ? (scan.orphans.notifications + scan.orphans.push_subscriptions + scan.orphans.notification_preferences) : 0;
  const visibleUsers = scan ? scan.users.filter((u) => !tenantIds.has(u.tenant_id)) : [];
  const selectedUserIds = visibleUsers.filter((u) => userIds.has(u.id)).map((u) => u.id);
  const nothing = tenantIds.size === 0 && selectedUserIds.length === 0 && !(clearOrphans && orphanTotal > 0);

  const purge = async () => {
    setBusy(true);
    try {
      const { data } = await API.post("/platform/test-data/purge", { tenant_ids: [...tenantIds], user_ids: selectedUserIds, clear_orphans: clearOrphans && orphanTotal > 0, confirm });
      setResult(data);
      toast.success(data.message);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't remove test data");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="test-data-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Broom size={18} /> Remove test data</DialogTitle>
          <DialogDescription>Workspaces and logins that look like test fixtures. Tick what should go — the Fixlr Platform workspace and your own login are never listed. Removal is permanent.</DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 dark:bg-emerald-950/20 p-4 text-sm" data-testid="test-data-result">
            <p className="font-semibold">{result.message}</p>
            <ul className="mt-2 text-xs text-muted-foreground space-y-0.5">
              {result.tenants.map((t) => <li key={t.id}>Workspace {t.name} ({t.slug}) — {t.records.toLocaleString()} record{t.records === 1 ? "" : "s"}{t.subscription_cancelled ? " · card subscription cancelled" : ""}</li>)}
              {result.users.map((u) => <li key={u.id}>Login {u.email}{u.was_platform_admin ? " (was a platform admin)" : ""}</li>)}
              {Object.entries(result.orphans || {}).map(([k, v]) => <li key={k}>{v.toLocaleString()} orphaned {k.replace(/_/g, " ")}</li>)}
            </ul>
          </div>
        ) : loading && !scan ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-8 justify-center" data-testid="test-data-loading"><Spinner size={16} className="animate-spin" /> Scanning…</div>
        ) : scan && (
          <div className="space-y-5 text-sm">
            <section>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Workspaces <span className="font-normal">({scan.tenants.length})</span></h4>
              {scan.tenants.length === 0 ? <p className="text-xs text-muted-foreground" data-testid="test-data-no-tenants">No test-looking workspaces.</p> :
                <div className="space-y-1.5">{scan.tenants.map((t) => <TestDataTenantRow key={t.id} t={t} checked={tenantIds.has(t.id)} onToggle={() => toggle(tenantIds, setTenantIds, t.id)} />)}</div>}
            </section>
            <section>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Logins in the remaining workspaces <span className="font-normal">({visibleUsers.length})</span></h4>
              {visibleUsers.length === 0 ? <p className="text-xs text-muted-foreground" data-testid="test-data-no-users">No test-looking logins{scan.users.length ? " outside the ticked workspaces" : ""}.</p> :
                <div className="space-y-1.5">{visibleUsers.map((u) => <TestDataUserRow key={u.id} u={u} checked={userIds.has(u.id)} onToggle={() => toggle(userIds, setUserIds, u.id)} />)}</div>}
              <div className="flex gap-2 mt-2">
                <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Add another login by e-mail…" className="h-8 text-xs" onKeyDown={(e) => e.key === "Enter" && addEmail()} data-testid="test-data-add-email" />
                <Button size="sm" variant="outline" onClick={addEmail} disabled={!email.trim() || loading} data-testid="test-data-add-email-btn">Add</Button>
              </div>
            </section>
            <label className="flex items-start gap-2 rounded-md border border-border px-3 py-2 cursor-pointer" data-testid="test-data-orphans-row">
              <Checkbox checked={clearOrphans} onCheckedChange={(v) => setClearOrphans(!!v)} className="mt-0.5" data-testid="test-data-orphans-check" />
              <span className="text-xs">Also clear <strong data-testid="test-data-orphans-count">{orphanTotal.toLocaleString()}</strong> stale notifications / push subscriptions left behind by logins that no longer exist{scan.orphans.users ? ` (${scan.orphans.users} former logins)` : ""}.</span>
            </label>
            <div className="rounded-md border border-red-200 bg-red-50 dark:bg-red-950/20 p-3">
              <p className="text-xs text-red-800 dark:text-red-300 flex items-center gap-1.5"><Warning size={14} weight="fill" /> This permanently deletes {tenantIds.size} workspace{tenantIds.size === 1 ? "" : "s"} with everything in them, {selectedUserIds.length} login{selectedUserIds.length === 1 ? "" : "s"}{clearOrphans && orphanTotal ? ` and ${orphanTotal.toLocaleString()} stale notifications` : ""}. Type <strong>REMOVE</strong> to confirm.</p>
              <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="REMOVE" className="h-8 text-xs mt-2 max-w-[200px]" data-testid="test-data-confirm-input" />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="test-data-close-btn">{result ? "Close" : "Cancel"}</Button>
          {!result && <Button variant="destructive" onClick={purge} disabled={busy || loading || nothing || confirm.trim() !== "REMOVE"} data-testid="test-data-purge-btn">
            {busy ? <Spinner size={14} className="animate-spin mr-1" /> : <Broom size={14} className="mr-1" />} Remove selected
          </Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
